import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  UniversalPage,
  UniversalTable,
  UniversalEmptyState,
  Card,
  Button,
  Modal,
  ModalBody,
  StatusBadge,
  type UniversalTableColumn,
  SearchField,
} from '@afrilink/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { Download, Phone } from 'lucide-react';
import {
  salesService,
  quotationUrgency,
  packageLabel,
  QUOTATION_URGENCY_TONE,
  type QuotationRow,
} from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { toCsv, downloadCsv } from '@/shared/utils/csvExport';
import { AddNoteForm } from '../components/AddNoteForm';
import { QuotationPayLinkButton } from '../components/QuotationPayLinkButton';
import { WithdrawQuotationButton } from '../components/WithdrawQuotationButton';

/**
 * SalesQuotationsPage — SM-R3.
 *
 * The one screen that answers "what lapses this week, and who do I phone?".
 * Before it existed, `quote_expires_at` was rendered on the lead detail page
 * only, so a manager had to open every lead in turn to find out — which is how
 * QUO-2026-000001 came within days of auto-cancelling with the customer's money
 * already banked (SALES_OPS_REVIEW_2026-07-27 §B.4).
 *
 * Read-through over the existing RLS-scoped views (AD-27) — no new view, no EF.
 * Deep-linkable: /sales/quotations?within=7 is what the dashboard card targets.
 */

type FilterKey = 'all' | 'critical' | 'week' | 'expired';

const FILTERS: Array<{ key: FilterKey; label: string; hint: string }> = [
  { key: 'all', label: 'All live', hint: 'Every quotation not yet paid or cancelled' },
  { key: 'critical', label: 'Next 2 days', hint: 'Lapses within 48 hours — phone today' },
  { key: 'week', label: 'Next 7 days', hint: 'Lapses this week' },
  { key: 'expired', label: 'Lapsed', hint: 'Past validity; cancelled on the next billing-cron pass' },
];

function zar(n: number | null): string {
  if (n === null) return '—';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n);
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
}

export default function SalesQuotationsPage() {
  const { staffRole } = useStaffRole();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<QuotationRow[]>([]);
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<QuotationRow | null>(null);

  // ?within=2|7 from the dashboard card; anything else falls back to "all".
  const within = params.get('within');
  const filter: FilterKey =
    within === '2' ? 'critical' : within === '7' ? 'week' : (params.get('filter') as FilterKey) || 'all';

  // Extracted so a withdrawal can re-pull the list. Without this the withdrawn
  // row would linger until a manual refresh, which reads as "it didn't work".
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await salesService.listQuotations();
      setRows(data);
      const ids = data.map((r) => r.assigned_agent_id).filter((x): x is string => !!x);
      setAgentNames(await salesService.resolveAgentNames(ids));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      critical: rows.filter((r) => r.days_left !== null && r.days_left >= 0 && r.days_left <= 2).length,
      week: rows.filter((r) => r.days_left !== null && r.days_left >= 0 && r.days_left <= 7).length,
      expired: rows.filter((r) => r.days_left !== null && r.days_left < 0).length,
    }),
    [rows]
  );

  /**
   * SEARCH ONLY — deliberately NO `PeriodFilter` on this page.
   *
   * The `days_left` chips above are ALREADY this page's date control, and they
   * are a better one: the question a quotations list exists to answer is "what
   * lapses soon", not "what was issued in March". Adding a period filter would
   * put two temporal controls with different meanings side by side — one
   * counting forward to expiry, one backward over issue date — and a user would
   * reasonably expect them to agree. They would not.
   *
   * Rule T-7's spirit applies: offer the control that answers the page's
   * question, not the one the pattern happens to supply.
   */
  const table = useTableFilters<QuotationRow>({
    rows,
    searchText: (r) =>
      [
        r.quote_reference ?? '',
        r.full_name,
        r.email ?? '',
        r.company ?? '',
        r.plan_name ?? '',
        r.sku_code ?? '',
      ].join(' '),
    facets: useMemo(
      () => [
        (r: QuotationRow) => {
          if (filter === 'critical') return r.days_left !== null && r.days_left >= 0 && r.days_left <= 2;
          if (filter === 'week') return r.days_left !== null && r.days_left >= 0 && r.days_left <= 7;
          if (filter === 'expired') return r.days_left !== null && r.days_left < 0;
          return true;
        },
      ],
      [filter],
    ),
  });

  const visible = table.rows;

  const pipelineValue = useMemo(
    () => visible.reduce((sum, r) => sum + Number(r.amount ?? 0), 0),
    [visible]
  );

  function setFilter(key: FilterKey) {
    const next = new URLSearchParams(params);
    next.delete('within');
    if (key === 'all') next.delete('filter');
    else next.set('filter', key);
    setParams(next, { replace: true });
  }

  /** Chase list — raw numbers, not formatted strings, so Excel can total them. */
  function exportCallList() {
    const header = [
      'Name', 'Company', 'Phone', 'Email', 'Quote', 'Amount (ZAR)',
      'Package', 'Domain', 'Issued', 'Expires', 'Days left', 'Agent',
    ];
    const body = visible.map((r) => [
      r.full_name,
      r.company ?? '',
      r.phone ?? '',
      r.email ?? '',
      r.quote_reference ?? '',
      r.amount ?? '',
      packageLabel(r) ?? '',
      r.domain_name ?? '',
      r.ordered_at ? new Date(r.ordered_at).toISOString().slice(0, 10) : '',
      r.quote_expires_at ? new Date(r.quote_expires_at).toISOString().slice(0, 10) : '',
      r.days_left ?? '',
      r.assigned_agent_id ? (agentNames[r.assigned_agent_id] ?? '') : 'Unassigned',
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`sighthost-quotation-chase-list-${filter}-${stamp}.csv`, toCsv([header, ...body]));
  }

  const columns = useMemo<UniversalTableColumn<QuotationRow>[]>(
    () => [
      {
        key: 'full_name',
        header: 'Lead',
        render: (r) => (
          <div className="min-w-0">
            <Link to={`/sales/leads/${r.customer_id}`} className="font-medium text-primary hover:underline">
              {r.full_name}
            </Link>
            {r.company && <div className="truncate text-xs text-muted-foreground">{r.company}</div>}
          </div>
        ),
      },
      {
        key: 'phone',
        header: 'Phone',
        render: (r) =>
          r.phone ? (
            <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 tabular-nums text-primary hover:underline">
              <Phone className="h-3 w-3" aria-hidden /> {r.phone}
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      { key: 'quote_reference', header: 'Quote', render: (r) => <span className="tabular-nums text-xs">{r.quote_reference ?? '—'}</span> },
      {
        key: 'plan_name',
        header: 'Package',
        // packageLabel, not plan_name: plan_name is hosting-only, so Studio /
        // Care / one-off quotations rendered as a bare dash (D2, 2026-07-29).
        render: (r) => packageLabel(r) ?? <span className="text-muted-foreground">—</span>,
      },
      { key: 'amount', header: 'Amount', align: 'right', render: (r) => <span className="tabular-nums">{zar(r.amount)}</span> },
      { key: 'ordered_at', header: 'Issued', render: (r) => <span className="tabular-nums text-xs">{shortDate(r.ordered_at)}</span> },
      { key: 'quote_expires_at', header: 'Expires', render: (r) => <span className="tabular-nums text-xs">{shortDate(r.quote_expires_at)}</span> },
      {
        key: 'days_left',
        header: 'Days left',
        render: (r) => {
          const u = quotationUrgency(r.days_left);
          const label =
            u === 'unknown' ? 'No date'
            : u === 'expired' ? `Lapsed ${Math.abs(r.days_left as number)}d ago`
            : r.days_left === 0 ? 'Today'
            : `${r.days_left}d`;
          return <StatusBadge tone={QUOTATION_URGENCY_TONE[u]}>{label}</StatusBadge>;
        },
      },
      {
        key: 'assigned_agent_id',
        header: 'Agent',
        render: (r) =>
          r.assigned_agent_id ? (
            agentNames[r.assigned_agent_id] ?? r.assigned_agent_id.slice(0, 8)
          ) : (
            <span className="text-muted-foreground">Unassigned</span>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <div className="flex items-center justify-end gap-1">
            <QuotationPayLinkButton
              customerProfileId={r.customer_id}
              customerName={r.full_name}
              label="Pay link"
            />
            <Link to={`/sales/leads/${r.customer_id}`}>
              <Button size="sm" variant="ghost" title="Open the lead to confirm an EFT payment or resend the quotation">
                Open
              </Button>
            </Link>
            <Button size="sm" variant="ghost" onClick={() => setNoteFor(r)} aria-label={`Add note for ${r.full_name}`}>
              Note
            </Button>
            <WithdrawQuotationButton
              quotation={{
                invoice_id: r.invoice_id,
                quote_number: r.quote_reference ?? '',
                amount: r.amount,
                plan_name: r.plan_name,
                sku_code: r.sku_code,
                domain_name: r.domain_name,
              }}
              onWithdrawn={load}
              label=""
            />
          </div>
        ),
      },
    ],
    [agentNames, load]
  );

  return (
    <UniversalPage
      title="Quotations"
      description={staffRole === 'admin' ? 'Cross-company — live quotations by expiry' : 'Live quotations by expiry'}
      actions={
        visible.length > 0 ? (
          <Button variant="outline" size="sm" onClick={exportCallList}>
            <Download className="h-4 w-4" /> Export call list
          </Button>
        ) : undefined
      }
    >
      {error && <Card className="border-danger/30 bg-danger/5 p-4 text-sm text-danger">{error}</Card>}

      {/* Urgency filters — counts are always of the FULL set, so a manager can
          see there are 8 critical rows even while looking at another filter. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            title={f.hint}
            aria-pressed={filter === f.key}
            className={
              filter === f.key
                ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-semibold text-primary'
                : 'rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground'
            }
          >
            {f.label}
            <span className="ml-1.5 tabular-nums opacity-70">{loading ? '·' : counts[f.key]}</span>
          </button>
        ))}
        {!loading && visible.length > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {visible.length} shown · <span className="font-semibold tabular-nums text-foreground">{zar(pipelineValue)}</span> at stake
          </span>
        )}
      </div>

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : visible.length === 0 ? (
        <UniversalEmptyState
          title={filter === 'all' ? 'No live quotations' : 'Nothing in this window'}
          description={
            filter === 'all'
              ? 'Quotations appear here from the moment an agent issues one until it is paid or cancelled.'
              : 'Try a wider window — the counts above show what exists.'
          }
          action={
            filter === 'all' ? (
              <Link to="/sales/leads">
                <Button variant="outline">Go to leads</Button>
              </Link>
            ) : (
              <Button variant="outline" onClick={() => setFilter('all')}>Show all live</Button>
            )
          }
        />
      ) : (
        // Keyed by invoice_id, NOT customer_id: since 2026-08-05 this list is
        // one row per QUOTATION, and a lead may hold several at once. Keying by
        // customer would collapse them into one React key and mis-render.
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <SearchField
              value={table.q}
              onValueChange={table.setQ}
              pending={table.isSearching}
              placeholder="Search reference, customer, company, product"
              className="ml-auto w-full max-w-sm"
            />
          </div>
          <UniversalTable
            columns={columns}
            data={visible}
            getRowKey={(r) => r.invoice_id}
            caption={table.caption}
          />
        </>
      )}

      {noteFor && (
        <Modal
          open
          onOpenChange={(o) => { if (!o) setNoteFor(null); }}
          title={`Add note — ${noteFor.full_name}`}
          description="Log the outcome of the chase call. Notes are visible to all sales staff who can see this lead, and are append-only."
          size="wide"
        >
          <ModalBody>
            <AddNoteForm customerProfileId={noteFor.customer_id} autoFocus onAdded={() => setNoteFor(null)} />
          </ModalBody>
        </Modal>
      )}
    </UniversalPage>
  );
}
